import { Flex, Spin } from "antd"
import React, { type ReactNode, useEffect, useState } from "react"
import { Navigate } from "react-router-dom"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { invoicesHiddenForRole } from "../../../utils/invoices-visibility.ts"

const PERMISSIONS_WAIT_MS = 8000

interface Props {
  children: ReactNode
}

const InvoicesAvailabilityGate = ({ children }: Props) => {
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
  const invoicesHidden = invoicesHiddenForRole(roleId)
  const [waitExpired, setWaitExpired] = useState(false)

  useEffect(() => {
    if (roleId !== undefined) return
    const timer = setTimeout(() => setWaitExpired(true), PERMISSIONS_WAIT_MS)
    return () => clearTimeout(timer)
  }, [roleId])

  // On a cold boot the profile loads asynchronously; "unknown" must wait, not
  // redirect — but only for a bounded time, so a failed profile fetch can't
  // strand the user on a spinner.
  if (roleId === undefined && !waitExpired) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ minHeight: "60vh" }}
        data-testid="invoices-gate-loading"
      >
        <Spin />
      </Flex>
    )
  }

  if (invoicesHidden) {
    return <Navigate to={RoutesPaths.home} replace />
  }

  return <>{children}</>
}

export default InvoicesAvailabilityGate
